import React from 'react';
import { Breadcrumb, CampaignIcon, EditableTitle } from './components';
import './styles/index.css';

/**
 * CampaignHeader - Shared header component for all views
 * 
 * Renders:
 * - Breadcrumb navigation for nested campaigns
 * - Clickable campaign icon with IconPicker
 * - Editable campaign name (click to edit)
 * 
 * Progress bars are NOT included - each view handles its own progress display
 * since styles/formats vary (bar, segmented, text-only).
 * 
 * @param {string} name - Campaign name
 * @param {string} icon - Campaign icon (emoji, fa:name, or R2 URL)
 * @param {Array} breadcrumbPath - Array of parent campaigns [{id, name, icon}]
 * @param {Function} onRename - Callback when name is changed (omit to disable editing)
 * @param {Function} onIconChange - Callback when icon is changed
 * @param {Function} onSelectSprint - Callback when breadcrumb item is clicked
 * @param {string} classPrefix - CSS class prefix for styling (default: 'campaign-header')
 * @param {React.ReactNode} children - Optional children (e.g., progress bar)
 */
const CampaignHeader = ({ 
  name,
  icon,
  breadcrumbPath = [],
  onRename,
  onIconChange,
  onSelectSprint,
  classPrefix = 'campaign-header',
  children,
}) => {
  if (!name) return null;

  return (
    <div className={`${classPrefix}`}>
      {/* Title row - breadcrumb + icon + name */}
      <div className={`${classPrefix}__title-row`}>
        <Breadcrumb 
          path={breadcrumbPath} 
          onSelectSprint={onSelectSprint}
          classPrefix={classPrefix}
        />
        
        <CampaignIcon 
          icon={icon} 
          onIconChange={onIconChange}
          classPrefix={classPrefix}
        />
        
        <EditableTitle 
          name={name} 
          onRename={onRename}
          classPrefix={classPrefix}
        />
      </div>

      {/* Optional children - typically progress bars */}
      {children}
    </div>
  );
};

export default CampaignHeader;

// Also export subcomponents for advanced usage
export { Breadcrumb, CampaignIcon, EditableTitle } from './components';
export { default as useTitleEditor } from './hooks/useTitleEditor';
